import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type EdgeChange,
  type IsValidConnection,
  type NodeChange,
  type NodeTypes,
  type OnDelete,
  type OnInit,
  type OnReconnect,
  type ReactFlowInstance,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { Spinner } from '../../../../common/components';
import { getMapBy } from '../../../../common/utils';
import { useSystemClassifierSelectOptions } from '../../../../configuration';
import { AlgorithmGraph } from '../../AlgorithmGraph';
import { AlgorithmGraphCta } from '../../AlgorithmGraphCta';
import type { AlgorithmSourceFormDataType } from '../../sources/schema/types';
import type {
  AlgorithmJoinsAndMappingsGraphEdgeType,
  AlgorithmJoinsAndMappingsGraphNodeType,
  AlgorithmJoinsAndMappingsGraphValidFormDataType,
} from '../schema/types';
import { type AlgorithmJoinHandleId, parseHandleId } from '../utils';
import { AlgorithmJoinsAndMappingsGraphCta } from './AlgorithmJoinsAndMappingsGraphCta';
import { AlgorithmJoinsWithMappingsGraphContext } from './AlgorithmJoinsWithMappingsGraphContext';
import { AlgorithmJoinsWithMappingsGraphLayouter } from './AlgorithmJoinsWithMappingsGraphLayouter';
import { AlgorithmMappingNode } from './nodeTypes/algorithmMapping/AlgorithmMappingNode';
import { AlgorithmSourceNode } from './nodeTypes/algorithmSource/AlgorithmSourceNode';
import { ConstantNode } from './nodeTypes/constant/ConstantNode';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmJoinsWithMappingsGraph.translations.json';
import { AlgorithmType } from '../../schema/zAlgorithmType';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-joins-with-mappings-graph',
);
addTranslations(translations);

export interface AlgorithmJoinsWithMappingsGraphProps
  extends PropsWithChildren {
  algorithmType: AlgorithmType;
  sources: AlgorithmSourceFormDataType[];
  readOnly?: boolean;
}

export const AlgorithmJoinsWithMappingsGraph: FC<
  AlgorithmJoinsWithMappingsGraphProps
> = (props) => {
  const { algorithmType, sources, readOnly } = props;
  const { t } = useTranslation();

  const {
    JOIN_TYPE: joinTypeOptions,
    MAPPING_JOIN_TYPE: mappingJoinTypeOptions,
  } = useSystemClassifierSelectOptions(
    ['JOIN_TYPE', 'MAPPING_JOIN_TYPE'],
    'code',
  );
  const algorithmSourcesById = getMapBy(sources, 'id');

  const isDbSources = useMemo(
    () => sources.some((s) => s.sourceType === 'DB'),
    [sources],
  );

  const reactFlowInstance = useRef<ReactFlowInstance<
    AlgorithmJoinsAndMappingsGraphNodeType,
    AlgorithmJoinsAndMappingsGraphEdgeType
  > | null>(null);

  const handleInit: OnInit<
    AlgorithmJoinsAndMappingsGraphNodeType,
    AlgorithmJoinsAndMappingsGraphEdgeType
  > = useCallback((instance) => {
    reactFlowInstance.current = instance;
    void instance.fitView();
  }, []);

  const [nodeChanges, setNodeChanges] = useState<
    NodeChange<AlgorithmJoinsAndMappingsGraphNodeType>[]
  >([]);

  const handleNodeChanges = useCallback(
    (changes: NodeChange<AlgorithmJoinsAndMappingsGraphNodeType>[]) =>
      setNodeChanges(
        readOnly ? changes.filter(({ type }) => type !== 'remove') : changes,
      ),
    [readOnly],
  );

  const [edgeChanges, setEdgeChanges] = useState<
    EdgeChange<AlgorithmJoinsAndMappingsGraphEdgeType>[]
  >([]);

  const handleEdgeChanges = useCallback(
    (changes: EdgeChange<AlgorithmJoinsAndMappingsGraphEdgeType>[]) =>
      setEdgeChanges(
        readOnly ? changes.filter(({ type }) => type !== 'remove') : changes,
      ),
    [readOnly],
  );

  const [formNodes, formEdges] = useWatch<
    AlgorithmJoinsAndMappingsGraphValidFormDataType,
    ['nodes', 'edges']
  >({ name: ['nodes', 'edges'] });

  const nodes = useMemo(() => {
    const existingGraphNodesById = getMapBy(
      reactFlowInstance.current?.getNodes() || [],
      'id',
    );
    const nodes = formNodes.map(({ id, position, ...rest }) => {
      const existingNode = existingGraphNodesById.get(id);
      return {
        id,
        position,
        ...existingNode,
        ...rest,
      };
    });
    return nodeChanges.length ? applyNodeChanges(nodeChanges, nodes) : nodes;
  }, [formNodes, nodeChanges]);

  const edges = useMemo(() => {
    const existingGraphEdgesById = getMapBy(
      reactFlowInstance.current?.getEdges() || [],
      'id',
    );
    const edges = formEdges.map(({ id, ...rest }) => {
      const existingEdge = existingGraphEdgesById.get(id);
      return {
        id,
        ...rest,
        ...existingEdge,
      };
    });
    return edgeChanges.length ? applyEdgeChanges(edgeChanges, edges) : edges;
  }, [formEdges, edgeChanges]);

  const { setValue } =
    useFormContext<AlgorithmJoinsAndMappingsGraphValidFormDataType>();

  const addEdge = useCallback(
    (connection: Connection) => {
      const sourceHandle = connection.sourceHandle as AlgorithmJoinHandleId;
      const targetHandle = connection.targetHandle as AlgorithmJoinHandleId;
      const source = parseHandleId(sourceHandle);
      const target = parseHandleId(targetHandle);
      const newEdge: AlgorithmJoinsAndMappingsGraphEdgeType = {
        id: window.crypto.randomUUID(),
        source: source.nodeId,
        target: target.nodeId,
        sourceHandle,
        targetHandle,
      };
      setValue('edges', [...formEdges, newEdge], { shouldDirty: true });
    },
    [formEdges, setValue],
  );

  const deleteEdges = useCallback(
    (edges: AlgorithmJoinsAndMappingsGraphEdgeType[]) => {
      const edgeIdsToRemove = new Set(edges.map((e) => e.id));
      setValue(
        'edges',
        formEdges.filter(({ id }) => !edgeIdsToRemove.has(id)),
        { shouldDirty: true },
      );
    },
    [formEdges, setValue],
  );

  const deleteNodes = useCallback(
    (nodes: AlgorithmJoinsAndMappingsGraphNodeType[]) => {
      const nodeIdsToRemove = new Set(nodes.map((n) => n.id));
      setValue(
        'nodes',
        formNodes.filter(({ id }) => !nodeIdsToRemove.has(id)),
        { shouldDirty: true },
      );
    },
    [formNodes, setValue],
  );

  const handleDelete: OnDelete<
    AlgorithmJoinsAndMappingsGraphNodeType,
    AlgorithmJoinsAndMappingsGraphEdgeType
  > = useCallback(
    ({ nodes, edges }) => {
      // Unfortunately react-flow fires deletion handlers before it has actually deleted
      // elements from its internal state (and consequently un-rendered nodes), so we must
      // defer updating form state to avoid those two states being out of sync.
      setTimeout(() => {
        if (nodes.length > 0) {
          deleteNodes(nodes);
        }
        if (edges.length > 0) {
          deleteEdges(edges);
        }
      });
    },
    [deleteNodes, deleteEdges],
  );

  // when existing edge is connected to a different handle
  const handleReconnect: OnReconnect<AlgorithmJoinsAndMappingsGraphEdgeType> =
    useCallback(
      (oldEdge, newConnection) => {
        deleteEdges([oldEdge]);
        addEdge(newConnection);
      },
      [deleteEdges, addEdge],
    );

  const isValidConnection: IsValidConnection<AlgorithmJoinsAndMappingsGraphEdgeType> =
    useCallback(
      (connection) => {
        // All of our graph nodes have handles, so we are not handling any directly-to-node connections
        if (!connection.sourceHandle || !connection.targetHandle) {
          return false;
        }

        // Prevent self-connections
        if (connection.source === connection.target) {
          return false;
        }

        const sourceNode = nodes.find(({ id }) => id === connection.source);
        const targetNode = nodes.find(({ id }) => id === connection.target);

        // Prevent constant-constant connections
        if (
          sourceNode?.type === 'constant' &&
          targetNode?.type === 'algorithmSource'
        ) {
          const { itemIndex: targetItemIndex } = parseHandleId(
            connection.targetHandle as AlgorithmJoinHandleId,
          );
          const targetItem = targetNode?.data.items?.[targetItemIndex];
          if ('constantValue' in targetItem) {
            return false;
          }
        }

        // Prevent connections to subsequent algorithm source joins (as per SQL join order)
        if (
          sourceNode?.type === 'algorithmSource' &&
          targetNode?.type === 'algorithmSource' &&
          nodes.indexOf(sourceNode) > nodes.indexOf(targetNode)
        ) {
          return false;
        }

        return true;
      },
      [nodes],
    );

  const [isLaidOut, setIsLaidOut] = useState(nodes.length === 0);

  const handleLayoutReady = useCallback(() => {
    reactFlowInstance.current?.fitView();
    setIsLaidOut(true);
  }, []);

  return (
    <>
      {!isLaidOut && <Spinner />}
      <AlgorithmJoinsWithMappingsGraphContext
        value={{
          algorithmSourcesById,
          readOnly,
          joinTypeOptions,
          mappingJoinTypeOptions,
          isDbSources,
        }}
      >
        <AlgorithmGraph
          nodes={nodes}
          onNodesChange={handleNodeChanges}
          edges={edges}
          onEdgesChange={handleEdgeChanges}
          onConnect={readOnly ? undefined : addEdge}
          onReconnect={readOnly ? undefined : handleReconnect}
          onDelete={readOnly ? undefined : handleDelete}
          isValidConnection={isValidConnection}
          nodeTypes={nodeTypes}
          onInit={handleInit}
          nodesConnectable={!readOnly}
          edgesReconnectable={!readOnly}
          style={{ opacity: isLaidOut ? 1 : 0, transition: 'opacity 0.3s' }}
        >
          {nodes.length === 0 ? (
            readOnly ? (
              <AlgorithmGraphCta>
                <h5>{t('no-joins-nor-mappings')}</h5>
              </AlgorithmGraphCta>
            ) : (
              <AlgorithmJoinsAndMappingsGraphCta
                algorithmType={algorithmType}
              />
            )
          ) : (
            <>
              <AlgorithmJoinsWithMappingsGraphLayouter
                onReady={handleLayoutReady}
              />
              {isLaidOut && !readOnly && (
                <UpdateEdgeCoordinatesOnFormStateChange />
              )}
            </>
          )}
        </AlgorithmGraph>
      </AlgorithmJoinsWithMappingsGraphContext>
    </>
  );
};

const nodeTypes = {
  algorithmSource: AlgorithmSourceNode,
  algorithmMapping: AlgorithmMappingNode,
  constant: ConstantNode,
} as NodeTypes;

const UpdateEdgeCoordinatesOnFormStateChange: FC = () => {
  const { errors, isSubmitting } = useFormState();
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    if (isSubmitting) {
      return;
    }
    updateNodeInternals(reactFlowInstance.getNodes().map((n) => n.id));
  }, [isSubmitting, errors, reactFlowInstance, updateNodeInternals]);

  return null;
};
